type dataValue = string | boolean | number | undefined | null;

/**
 * Transforms an HTML element dataset into an object with proper data types.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dataset
 *
 * Logic borrowed from `jQuery.data()` source code.
 *
 * Please note that some number strings will NOT be converted to numbers.
 * From http://api.jquery.com/data/ :
 *
 * > Every attempt is made to convert the string to a JavaScript value (this
 * includes booleans, numbers, objects, arrays, and null). A value is only
 * converted to a number if doing so doesn't change the value's representation.
 * For example, "1E02" and "100.000" are equivalent as numbers (numeric value
 * 100) but converting them would alter their representation so they are left as
 * strings. The string value "100" is converted to the number 100.
 *
 * @export
 * @param {DOMStringMap} dataset Element dataset
 * @returns {Object} An `Object` with proper data types.
 */
export default function getTypedDataset(dataset: DOMStringMap): Record<string, dataValue> {
  const rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/;

  return Object.keys(dataset).reduce((obj, key) => {
    const data = dataset[key];
    let value: dataValue = data;

    if (data === 'true') {
      value = true;
    }

    if (data === 'false') {
      value = false;
    }

    if (data === 'null') {
      value = null;
    }

    // Only convert to a number if it doesn't change the string
    // NOTE: some number strings will NOT be converted into numbers.
    // '2.1' === `${+'2.1'}`, but '2.0' !== `${+'2.0'}`
    // so '2.1' will become a number, but '2.0' will remain a string
    if (data && data === `${+data}`) {
      value = +data;
    }

    if (data && rbrace.test(data)) {
      value = JSON.parse(data) as string;
    }

    return { ...obj, [key]: value };
  }, {});
}
